import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationStart, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingScreenService } from 'app/shared/services/loading-screen/loading-screen.service';
import { TermsConditionsComponent } from 'app/terms-conditions/_pages/terms-conditions.component';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LanguageService } from '../../shared/services/language.service';
import { SharedService } from '../../shared/services/shared.service';
import { Translations, TranslationService } from '../../shared/services/translation.service';
import { Country, duplicateUsernameValidator, ReferenceValue, State, strictEmailValidator, UploadedFile } from '../_models/customer-form.model';
import { CustomerFormService } from '../_services/customer-form.service';

export enum FormStep {
  CompanyDetails = 1,
  UserAccounts = 2,
  Review = 3,
}

@Component({
  selector: 'app-customer-form',
  standalone: true,
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatSelectModule,
    HttpClientModule,
    MatIconModule,
    TranslateModule,
    MatTooltipModule
  ],
  providers: [LanguageService, TranslationService],
})
export class CustomerFormComponent implements OnInit, OnDestroy {
  errorMessages: { [key: string]: string } = {};
  backendErrorMessages: string[] = [];

  customerForm!: FormGroup;
  currentStep = 1;
  termsAgreed = false;
  hasReadTerms = false;
  termsAgreedBy = '';
  isReadChecked = false;
  isAgreedByEditable = true;
  showReview = false;
  submitted = false;
  isReviewConfirmed = false;
  customerIdGenerated = false;
  showThankYouPoster = false;
  step = FormStep.CompanyDetails;
  formSubmitted = false;
  isLoading = false;
  private submitInFlight = false;

  readonly TOTAL_STEPS = FormStep.Review;
  translations!: Translations;
  private translationSubscription!: Subscription;
  businessLicenseFiles: UploadedFile[] = [];
  showFileError = false;
  agreedByVisible = true;
  countries: Country[] = [];
  states: State[] = [];
  taxIdTypes: ReferenceValue[] = [];

  // Contract details captured from Terms & Conditions dialog
  private contractId: string | null = null;
  private contractContent: string = '';
  private contractFullName: string = '';
  private contractSignature: string = '';

  // Expose enum to template
  FormStep = FormStep;

  readonly socialMediaSoftwareOptions: string[] = [
    'weChatId',
    // 'WhatsApp',
    // 'Telegram',
    // 'LINE',
    // 'KakaoTalk',
  ];

  constructor(
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
    private router: Router,
    private dialog: MatDialog,
    public sharedService: SharedService,
    private snackBar: MatSnackBar,
    private customerFormService: CustomerFormService,
    private toastr: ToastrService,
    private ngZone: NgZone,
    private loadingScreenService: LoadingScreenService,
    private route: ActivatedRoute
  ) {
    // Listen for navigation events
     this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const currentUrl = this.router.url;
        const targetUrl = event.url;

        // Save form state on ANY navigation away from the form
        if (currentUrl.includes('onboarding')) {
          const formState = {
            formData: {
              ...this.customerForm.value,
              userList: this.getUserListData(),
              businessLicenseFiles: this.businessLicenseFiles.map((file) => ({
                id: file.id,
                name: file.file.name,
                type: file.file.type,
                url: file.url,
              })),
            },
            step: this.step,
          };          
        }

        if (
          !targetUrl.includes('terms-conditions') &&
          !targetUrl.includes('onboarding')
        ) {
          sessionStorage.removeItem('formState');
          sessionStorage.removeItem('fromCustomerForm');
          this.sharedService.clearFormData();
        }
      }
    });

    this.initializeForm();
    // Subscribe to translations
    this.translationSubscription =
      this.translationService.translations$.subscribe(
        (translations: Translations) => {
          this.ngZone.run(() => {
            this.translations = translations;
          });
        }
      );

    // Subscribe to terms agreement state
    this.sharedService.getTermsAgreed().subscribe((agreed) => {
      this.termsAgreed = agreed;
      if (agreed) {
        this.hasReadTerms = true;
        this.isAgreedByEditable = true;
        this.customerForm.patchValue({
          agreed: true,
        });
        this.customerForm.get('agreed')?.disable();
      }
    });

    // Subscribe to step changes
    this.sharedService.getCurrentStep().subscribe((step) => {
      this.step = step;
    });

    // console.log("step in constructor", this.step);
    // console.log("value in constructor", this.FormStep.UserAccounts);
    if (this.step === this.FormStep.UserAccounts) {
      // console.log("Usersform");
      this.addUser();
    }

    this.customerForm.get('language')?.valueChanges.subscribe(selectedLang => {
      this.setDefaultLanguageForUsers(selectedLang);
    });
  }

  setDefaultLanguageForUsers(language: string): void {
    this.usersArray.controls.forEach(userGroup => {
      if (!userGroup.get('language')?.value) {
        userGroup.get('language')?.setValue(language);
      }
    });
  }

  private initializeForm(): void {
   
      // console.log('Initializing new form');
      this.initForm();
  }

  ngOnInit(): void {
    this.loadingScreenService.show();
    if(this.route.snapshot.queryParamMap.get('isChecked') === 'true'){
      this.isReadChecked = true;
    } else{
      this.isReadChecked = false;
    }
    this.loadErrorMessages();

    // Subscribe to form value changes
    this.customerForm.valueChanges.subscribe(() => {
      
    });

    // Subscribe to userList changes specifically
    this.usersArray.valueChanges.subscribe(() => {
      
    });

    this.loadTranslations();
    this.loadCountries();
    this.loadTaxIdTypes();
    // Subscribe to translations
    this.translationSubscription =
      this.translationService.translations$.subscribe(
        (translations: Translations) => {
          this.translations = translations;
          this.cdr.detectChanges();
        }
      );

    // Subscribe to terms agreement state
    this.sharedService.getTermsAgreed().subscribe((agreed) => {
      this.termsAgreed = agreed;
      if (agreed) {
        this.hasReadTerms = true;
        this.isAgreedByEditable = true;
        this.customerForm.patchValue({
          agreed: true,
        });
        this.customerForm.get('agreed')?.disable();
      }
    });

    // Check if returning from terms
    const fromTerms = sessionStorage.getItem('fromCustomerForm') === 'true';
    if (fromTerms) {
      // console.log('Returning from terms page, restoring form state...');

      // Clear the navigation flag
      sessionStorage.removeItem('fromCustomerForm');

      // Restore form state
      const savedState = sessionStorage.getItem('formState');
      if (savedState) {
        // console.log('Found saved form state');
        const { formData, step } = JSON.parse(savedState);
        // console.log('Parsed form data:', formData);

        // Restore business license files
        const savedFiles = sessionStorage.getItem('businessLicenseFiles');
        if (savedFiles) {
          // console.log('Restoring business license files');
          const parsedFiles = JSON.parse(savedFiles);
          this.businessLicenseFiles = parsedFiles.map((file: any) => ({
            id: file.id,
            file: new File([], file.name, { type: file.type }),
            url: file.url,
          }));
          // console.log('Restored files:', this.businessLicenseFiles);
        }

        // First restore the user array
        if (formData.userList && Array.isArray(formData.userList)) {
          // console.log('Restoring user list:', formData.userList);
          // Clear existing users
          while (this.usersArray.length) {
            this.usersArray.removeAt(0);
          }
          // Add saved users
          formData.userList.forEach((userData: any) => {
            if (!userData) return;

            const userForm = this.formBuilder.group({
              username: [
                userData.username || '',
                [Validators.required, Validators.minLength(2)],
              ],
              firstName: [
                userData.firstName || '',
                [Validators.required, Validators.minLength(2)],
              ],
              lastName: [userData.lastName || '', Validators.required],
              email: [
                userData.email || '',
                [Validators.required, Validators.email],
              ],
              mobile: [
                userData.mobile || '',
                [Validators.required, Validators.maxLength(15)],
              ],
              language: [userData.language || '', Validators.required],
              software: [
                userData.socialMediaAccounts?.software || userData.software || '',
              ],
              weChatId: [
                userData.socialMediaAccounts?.weChatId || userData.weChatId || '',
              ],
            });

            this.usersArray.push(userForm);
          });
        }

        // Now restore the rest of the form data
        this.customerForm.patchValue({
          ...formData,
          userList: undefined, // Don't patch userList again
        });

        // If country is selected, reload states
        if (formData.country) {
          // console.log('Restoring country and state data');
          const selectedCountry = this.countries.find(
            (country) => country.code === formData.country
          );
          if (selectedCountry) {
            this.customerFormService.getStates(selectedCountry.code).subscribe({
              next: (states: any[]) => {
                if (typeof states[0] === 'object') {
                  this.states = states as State[];
                } else {
                  this.states = states.map((stateName: string) => ({
                    id: stateName.toUpperCase().replace(/\s+/g, '_'),
                    name: stateName,
                    code: stateName.toUpperCase().replace(/\s+/g, '_'),
                  }));
                }
                // Re-select the state after states are loaded
                if (formData.state) {
                  // console.log('Restoring state:', formData.state);
                  this.customerForm.patchValue({ state: formData.state });
                }
                this.cdr.detectChanges();
              },
              error: (error) => {
                console.error('Error fetching states:', error);
              },
            });
          }
        }

        // Restore step
        this.step = step;
        this.sharedService.setCurrentStep(step);

        // Get agreement state and handle form accordingly
        this.sharedService.getTermsAgreed().subscribe((agreed) => {
          this.termsAgreed = agreed;
          this.isAgreedByEditable = true;

          if (agreed) {
            const currentAgreedBy = this.customerForm.get('agreedBy')?.value;
            this.customerForm.patchValue({
              agreed: true,
              agreedBy: currentAgreedBy || formData.agreedBy,
            });
            this.customerForm.get('agreed')?.disable();
          }
        });

        // console.log('Form state restored successfully');
        this.cdr.detectChanges();
      }
    }

    // Subscribe to step changes
    this.sharedService.getCurrentStep().subscribe((step) => {
      this.step = step;
    });
    this.subscribeToCompanyLanguageChanges();

    // console.log("step in ngoninit", this.step);
    // console.log("value in ngoninit", this.FormStep.UserAccounts);
    if (this.step === this.FormStep.UserAccounts) {
      // console.log("Usersform in oninit");
      this.addUser();
    }
    this.loadingScreenService.hide();
  }

  loadErrorMessages(): void {
    this.http
      .get<{ [key: string]: string }>(
        '/assets/i18n/en_AU/Common/customer-form.json'
      )
      .subscribe({
        next: (data) => {
          this.errorMessages = data;
        },
        error: (err) => {
          console.error('Failed to load error messages:', err);
        },
      });
  }

  ngOnDestroy() {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
    // Clean up object URLs
    this.businessLicenseFiles.forEach((file) => {
      URL.revokeObjectURL(file.url);
    });
  }

  onInputWithSpace(event: Event, controlName: string): void {
    //it can have spaces anywhere except for first charcter, also check the above validators for changes
    const input = event.target as HTMLInputElement;
    let currentValue = input.value.replace(/^\s+/, ''); // Removing complete spaces 
    const control = this.customerForm.get(controlName);

    if (control) {
      if (!control.touched) {
        control.markAsTouched(); // Mark as touched to trigger validation
      }
      const errors = control.errors || {};
      // Ensure at least 2 valid characters (excluding leading spaces)
      if (currentValue.trim().length < 2) {
        errors["minlength"] = true;
      } else {
        delete errors["minlength"];
      }
      control.setValue(currentValue, { emitEvent: false });
      control.setErrors(Object.keys(errors).length ? errors : null);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  onInputWithSpaceNew(event: Event, controlName: string, index: number): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value.trim(); // Removes leading and trailing spaces
  
    const controlPath = `userList.${index}.${controlName}`;
    const control = this.customerForm.get(controlPath);
  
    if (control) {
      if (!control.touched) {
        control.markAsTouched(); // Mark as touched to trigger validation
      }
  
      control.setValue(currentValue, { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    }
  }
  

  private initForm(): void {
    this.customerForm = this.formBuilder.group({
      name: ['', Validators.required],
      billingEmail: ['', [Validators.required, strictEmailValidator()]],
      taxId: [''],
      taxIdType: [''],
      language: ['', Validators.required],
      businessNumber: ['', [Validators.required]],
      country: ['', Validators.required],
      state: ['', Validators.required],
      streetAddress: ['', Validators.required],
      streetAddress2: [''],
      suburb: ['', Validators.required],
      city: [''],
      postCode: ['', Validators.required],
      agreed: [false, Validators.requiredTrue],
      agreedBy: ['', Validators.required],
      businessLicense: [[]],
      userList: this.formBuilder.array([]),
      reviewConfirmed: [false, Validators.requiredTrue],
    });
  }

  // Getter methods for form groups
  get usersArray() {
    return this.customerForm.get('userList') as FormArray;
  }

  onCountryChange(event: MatSelectChange) {
    this.loadingScreenService.show();
    const selectedCountry = this.countries.find(
      (country) => country.name === event.value
    );

    if (selectedCountry) {
      // Reset state selection and states array
      this.customerForm.patchValue({ state: null });
      this.states = [];
      
      this.customerFormService.getStates(selectedCountry.code).pipe(
        finalize(() => this.loadingScreenService.hide())
      ).subscribe({
        next: (states: any[]) => {
          // If states are objects, use them directly
          if (typeof states[0] === 'object') {
            this.states = states as State[];
          } else {
            // If states are strings, convert them to objects with unique codes
            this.states = states.map((stateName: string) => ({
              id: stateName.toUpperCase().replace(/\s+/g, '_'),
              name: stateName,
              code: stateName.toUpperCase().replace(/\s+/g, '_'),
            }));
          }
          // console.log('States loaded:', this.states);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error fetching states:', error);
          this.snackBar.open(
            'Failed to load states. Please try again.',
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
        },
      });
    } else {
      this.loadingScreenService.hide();
    }
  }

  // Add method to handle state selection
  onStateChange(event: MatSelectChange) {
    const selectedState = this.states.find(
      (state) => state.name === event.value
    );
    if (selectedState) {
      this.customerForm.patchValue({
        state: {
          id: selectedState.code,
          name: selectedState.name,
        },
      });
    }
  }

  addUser() {
    const defaultLanguage = this.customerForm.get('language')?.value || '';
    const userForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.minLength(2)]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['',Validators.required],
      email: ['',[Validators.email,Validators.required]],
      mobile: ['',Validators.required],
      language: [defaultLanguage,Validators.required],
      software: [''],
      weChatId: [''],
    });
    const size = this.usersArray.length
    this.usersArray.push(userForm);
    // Apply duplicate username validator to each control after push
    this.usersArray.controls.forEach((control) => {
      const usernameControl = control.get('username');
      usernameControl?.setValidators([
        Validators.required,
        Validators.minLength(2),
        duplicateUsernameValidator(this.usersArray),
      ]);
      usernameControl?.updateValueAndValidity({ onlySelf: true });
    });
    this.markAsPristine(this.usersArray.at(size - 1))
    // console.log("userArray",this.usersArray)
  }

  markAsPristine(control: AbstractControl | null) {
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key => {
        this.markAsPristine(control.get(key));
      });
    } else if (control instanceof FormArray) {
      const controls = (control as FormArray).controls;
      controls.forEach(formControl => {
        this.markAsPristine(formControl);
      });
    } else if (control instanceof FormControl) {
      control.markAsPristine();
      control.markAsUntouched();
    } else {
    }
  }

  removeUser(index: number) {
    this.usersArray.removeAt(index);
    
    // console.log('User removed, current users:', this.getUserListData());
  }

  onSoftwareChange(index: number): void {
    const userForm = this.usersArray.at(index);
    if (!userForm) return;

    const software = userForm.get('software')?.value;
    const softwareIdControl = userForm.get('weChatId');

    // Make softwareId required only when a software is selected.
    if (software) {
      softwareIdControl?.setValidators([Validators.required]);
    } else {
      softwareIdControl?.clearValidators();
    }
    softwareIdControl?.updateValueAndValidity({ emitEvent: false });

    // If it's not WeChat, clear the value (field is hidden).
    if (software !== 'weChatId') {
      userForm.patchValue({ weChatId: '' }, { emitEvent: false });
      softwareIdControl?.markAsUntouched();
      softwareIdControl?.markAsPristine();
    }
  }

  clearSoftwareSelection(event: Event, index: number): void {
    event.stopPropagation();
    event.preventDefault();

    const userForm = this.usersArray.at(index);
    if (!userForm) return;

    userForm.patchValue({ software: '', weChatId: '' });
    const softwareIdControl = userForm.get('weChatId');
    softwareIdControl?.clearValidators();
    softwareIdControl?.updateValueAndValidity({ emitEvent: false });
    softwareIdControl?.markAsUntouched();
    softwareIdControl?.markAsPristine();
    userForm.get('software')?.markAsUntouched();
    userForm.get('software')?.markAsPristine();
  }

  prepareSubmissionPayload() {
    throw new Error('Method not implemented.');
  }

    nextStep(): void {
    // console.log('Next button clicked, current step:', this.step);
    const currentFormGroup = this.getCurrentStepFormGroup();

    if (!this.validateCurrentStep()) {
      // console.log('Current step validation failed');
      this.markFormGroupTouched(this.getCurrentStepFormGroup());
      return;
    }

    if (this.step < this.TOTAL_STEPS) {
      this.step++;
      this.sharedService.setCurrentStep(this.step);
      window.scrollTo(0, 0);
      if (this.step === FormStep.UserAccounts && (this.customerForm.get('userList') as FormArray).length < 1) {
        this.addUser();
      } 
    }
  }

  prevStep(): void {
    if (this.step > FormStep.CompanyDetails) {
      
      this.step--;
      this.sharedService.setCurrentStep(this.step);
      window.scrollTo(0, 0);
    }
  }

  private validateCurrentStep(): boolean {
    // console.log('Validating step:', this.step);
    // Always save current data regardless of validation result
    if (this.step === FormStep.UserAccounts) {
      const result = this.usersArray.length > 0 && this.usersArray.valid;
      // console.log('User accounts validation result:', result);
      return result;
    }

    const result = this.canProceedToNextStep();
    // console.log('Form validation result:', result);
    return result;
  }

  private getCurrentStepFormGroup(): FormGroup | FormArray | null {
    switch (this.step) {
      case FormStep.UserAccounts:
        return this.usersArray;
      default:
        return this.customerForm;
    }
  }

  private subscribeToCompanyLanguageChanges(): void {
    const companyLanguageControl = this.customerForm.get('language');
    if (companyLanguageControl) {
      companyLanguageControl.valueChanges.subscribe(
        (selectedLanguage: string) => {
          this.usersArray.controls.forEach((control: AbstractControl) => {
            const userGroup = control as FormGroup;
            const userLanguageControl = userGroup.get('language');
            if (userLanguageControl && !userLanguageControl.value) {
              userLanguageControl.setValue(selectedLanguage);
            }
          });
        }
      );
    }
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray | null) {
    if (!formGroup) return;

    if (formGroup instanceof FormGroup) {
      Object.values(formGroup.controls).forEach((control) => {
        if (control instanceof FormGroup || control instanceof FormArray) {
          this.markFormGroupTouched(control);
        } else {
          control.markAsTouched();
        }
      });
    } else if (formGroup instanceof FormArray) {
      formGroup.controls.forEach((control) => {
        if (control instanceof FormGroup || control instanceof FormArray) {
          this.markFormGroupTouched(control);
        } else {
          control.markAsTouched();
        }
      });
    }
  }

    editStep(step: FormStep): void {
    this.showReview = false;
    this.step = step;
    this.isReadChecked = false;
  
    const agreedByControl = this.customerForm.get('agreedBy');
    if (agreedByControl) {
      agreedByControl.reset(); // Clear the value
    }
  
    const reviewConfirmedControl = this.customerForm.get('reviewConfirmed');
    if (reviewConfirmedControl) {
      reviewConfirmedControl.setValue(false); // Uncheck the checkbox
    }
  }
  
  onFileSelected(event: any) {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files as File[]).map((file: File) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const fileData = {
              file,
              id: this.generateUniqueId(),
              url: e.target.result,
            };
            resolve(fileData);
          };
          reader.readAsDataURL(file);
        });
      });

      Promise.all(newFiles).then((result: any[]) => {
        this.businessLicenseFiles = result;
        this.customerForm.get('businessLicense')?.setValue(result);
        this.showFileError = false;
      });
    }
  }

  generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  downloadFile(file: UploadedFile): void {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  deleteFile(file: UploadedFile, event: Event): void {
    event.stopPropagation();
    this.businessLicenseFiles = this.businessLicenseFiles.filter(
      (f) => f.id !== file.id
    );
    URL.revokeObjectURL(file.url);
  }

  onSubmit() {
    if (this.submitInFlight) return;
    this.loadingScreenService.show();
    // Check if review is confirmed
    if (this.customerForm.get('reviewConfirmed')?.value !== true) {
      console.warn('Review not confirmed. Form not submitted.');
      this.loadingScreenService.hide();
      return;
    }

    // Gather form values
    const formValue = this.customerForm.value;
    const verifiedEmail = sessionStorage.getItem('verifiedEmail');

    // Helper function to capitalize first letter of each word
    const capitalizeWords = (str: string) => {
      if (!str) return str;
      return str
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');
    };

    // Select state from form value
    const selectedState = this.states.find((s) => s.name === formValue.state);
    const stateObject = {
      id: formValue.state.id,
      name: formValue.state.name,
    };

    // Prepare the customer onboarding data object
    const customerOnBoarding = {
      name: capitalizeWords(formValue.name),
      billingEmail: formValue.billingEmail,
      businessNumber: formValue.businessNumber,
      loggedInEmail: verifiedEmail,
      taxId: formValue.taxId.toUpperCase(),
      taxIdType: formValue.taxIdType.toUpperCase(),
      language: formValue.language,
      country: capitalizeWords(formValue.country),
      state: stateObject,
      streetAddress: capitalizeWords(formValue.streetAddress),
      streetAddress2: capitalizeWords(formValue.streetAddress2),
      suburb: capitalizeWords(formValue.suburb),
      city: capitalizeWords(formValue.city),
      postCode: formValue.postCode,
      // agreed: formValue.agreed,
      // agreedBy: capitalizeWords(formValue.agreedBy),
      // reviewConfirmed: formValue.reviewConfirmed,
      commonContractOnBoardingDTO: {
        id: this.contractId,
        content: this.contractContent,
        fullName: this.contractFullName || capitalizeWords(formValue.agreedBy),
        signature: this.contractSignature,
      },
      userList: formValue.userList.map((user: any) => ({
        username: user.username,
        firstName: capitalizeWords(user.firstName),
        lastName: capitalizeWords(user.lastName),
        email: user.email,
        mobile: user.mobile,
        language: user.language,
        socialMediaAccounts: user.weChatId
          ? {
              software: user.software || 'WeChat',
              weChatId: user.weChatId,
            }
          : null,
      })),
    };

    // Submit as raw JSON (no files)
    const payload = { customerOnBoarding };

    // Submit the form data
    this.submitInFlight = true;
    this.customerFormService
      .submitCustomerForm(payload.customerOnBoarding)
      .pipe(
        finalize(() => {
          this.submitInFlight = false;
          this.loadingScreenService.hide();
        })
      )
      .subscribe({
      next: (response) => {
        // console.log('Form submitted successfully:', response);
        this.formSubmitted = true;

        this.toastr.success(
          this.translations.formSubmit,
          this.translations.success
        );

        // Clear session state and form data after successful submission
        sessionStorage.removeItem('formState');
        this.sharedService.clearFormData();

        setTimeout(() => {
          window.location.href = 'https://www.bptfl.com/';
        }, 5000);
      },
      error: (error) => {
        this.backendErrorMessages = []; // Clear previous error messages

        let errorMessage = error.error?.messageCode || this.translations.unexpectedError ;
       
        if (error.status === 0) {
          // Handle server connectivity issues
          errorMessage =  this.translations.connectionError;
        } else if (error.status === 400) {
          // Handle 400 status code errors
          const errorCode = error.error?.messageCode;
          if (errorCode && this.errorMessages[errorCode]) {
            // Match error code with the message from the JSON
            errorMessage = this.errorMessages[errorCode];
          } else {
            errorMessage = error.error?.messageCode || this.translations.tryAgain;
          }
        } else if (error.status === 401) {
          // Handle unauthorized errors
          errorMessage = this.translations.sessionExpired;
        }

        this.toastr.error(errorMessage, 'Error');
      },
    });
  }
  
  navigateToDashboard() {
    window.location.href = 'https://www.google.com/';
    // console.log('navigateToDashboard');
  }

  openTermsAndConditions() {
    // console.log('Opening terms and conditions');
    const dialogRef = this.dialog.open(TermsConditionsComponent, {
       width: "95%",
       height: "95%",
       maxWidth: '100vw',
       maxHeight: '100vh',
       panelClass: 'no-radius-dialog',
       autoFocus: false,
       data : {
        checked : this.isReadChecked,
       }
       
      // disableClose: true, // Prevent closing dialog without action
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result || typeof result === 'boolean') return;

      if (result.agreed) {
        this.isReadChecked = true;

        const dto = result.commonContractOnBoardingDTO;
        this.contractId = dto?.id ?? null;
        this.contractContent = dto?.content ?? '';
        this.contractFullName = dto?.fullName ?? (result.name || '');
        this.contractSignature = dto?.signature ?? '';

        this.customerForm.patchValue({
          agreed: true,
          agreedBy: result.name || '',
          reviewConfirmed: true,
        });
        this.customerForm.get('agreed')?.disable();
      }

      // Requirement: clicking Continue in dialog triggers submission.
      if (result.submitNow === true) {
        this.onSubmit();
      }
    });
    
    // const formState = {
    //   formData: {
    //     ...this.customerForm.value,
    //     userList: this.getUserListData(),
    //     businessLicenseFiles: this.businessLicenseFiles.map((file) => ({
    //       id: file.id,
    //       name: file.file.name,
    //       type: file.file.type,
    //       url: file.url,
    //     })),
    //   },
    //   step: this.step,
    // };

    const currentAgreed = this.customerForm.get('agreed')?.value;
    this.sharedService.setTermsAgreed(currentAgreed || false);
  }

  onTermsAgreementChange(event: MatCheckboxChange) {
    // console.log(
    //   'Terms agreement change:',
    //   event.checked,
    //   'hasReadTerms:',
    //   this.hasReadTerms
    // );

    // If terms haven't been read, prevent checking
    if (!this.hasReadTerms) {
      event.source.checked = false;
      return;
    }

    // If already agreed from terms page, prevent unchecking
    if (this.termsAgreed && this.hasReadTerms) {
      event.source.checked = true;
      // Disable only the checkbox
      this.customerForm.get('agreed')?.disable();
      return;
    }

    const isChecked = event.checked;
    // console.log('Setting checkbox to:', isChecked);

    // Update local state
    this.termsAgreed = isChecked;
    // Always allow editing of agreedBy field
    this.isAgreedByEditable = true;

    // Update form control
    this.customerForm.patchValue({
      agreed: isChecked,
    });

    // If checked and terms were read, disable only the checkbox
    if (isChecked && this.hasReadTerms) {
      this.customerForm.get('agreed')?.disable();
    }

    // Update shared service and save form
    this.sharedService.setTermsAgreed(isChecked);
    
  }

  // Add this method to save the current step before navigating to terms and conditions
  saveCurrentStep(): void {
    this.sharedService.setCurrentStep(this.step);
  }

  canProceedToNextStep(): boolean {
    switch (this.step) {
      case FormStep.CompanyDetails:
        const companyDetailsValid =
          this.customerForm.get('name')?.valid === true &&
          this.customerForm.get('billingEmail')?.valid === true &&
          this.customerForm.get('businessNumber')?.valid === true &&
          this.customerForm.get('language')?.valid === true &&
          this.customerForm.get('businessNumber')?.valid === true &&
          this.customerForm.get('country')?.valid === true &&
          this.customerForm.get('state')?.valid === true &&
          this.customerForm.get('streetAddress')?.valid === true &&
          this.customerForm.get('suburb')?.valid === true &&
          this.customerForm.get('postCode')?.valid === true;
        return companyDetailsValid;
  
      case FormStep.Review:
        return (
          // this.customerForm.get('agreedBy')?.valid === true &&
          // this.businessLicenseFiles.length > 0 &&
          this.customerForm.get('reviewConfirmed')?.value === true
        );
  
      case FormStep.UserAccounts:
        return this.usersArray.valid && this.usersArray.length > 0;
  
      default:
        return false;
    }
  }
  

  // Direct navigation method
  goToStep(targetStep: FormStep): void {
    // console.log(`Directly navigating to step ${targetStep}`);
    this.step = targetStep;
    this.sharedService.setCurrentStep(targetStep);
    window.scrollTo(0, 0);
  }

  private loadTranslations() {
    this.translationService.translations$.subscribe({
      next: (translations: Translations) => {
        this.translations = translations;
        this.cdr.detectChanges();
      },
      error: (error: Error) => {
        console.error('Error loading translations:', error);
        this.snackBar.open(
          'Error loading translations. Please try again later.',
          'Close',
          {
            duration: 5000,
          }
        );
      },
    });
  }

  loadCountries() {
    this.loadingScreenService.show();
    this.customerFormService.getCountries().pipe(
      finalize(() => this.loadingScreenService.hide())
    ).subscribe({
      next: (countries) => {
        this.countries = countries;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.snackBar.open(
          'Error loading countries. Please try again later.',
          'Close',
          {
            duration: 5000,
          }
        );
      },

    });
  }

  private loadTaxIdTypes() {
    // Using 'businessandtaxidentifiers' as the reference type internal code
    this.loadingScreenService.show();
    this.customerFormService
      .getTaxIdTypes('businessandtaxidentifiers')
      .pipe(finalize(() => this.loadingScreenService.hide()))
      .subscribe({
        next: (taxIdTypes) => {
          this.taxIdTypes = taxIdTypes;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading tax ID types:', error);
          this.snackBar.open(
            'Error loading tax ID types. Please try again later.',
            'Close',
            {
              duration: 5000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
        },
      });
  }

  private getUserListData(): any[] {
    return this.usersArray.controls.map((control) => ({
      username: control.get('username')?.value || '',
      firstName: control.get('firstName')?.value || '',
      lastName: control.get('lastName')?.value || '',
      email: control.get('email')?.value || '',
      mobile: control.get('mobile')?.value || '',
      language: control.get('language')?.value || '',
      software: control.get('software')?.value || '',
      weChatId: control.get('weChatId')?.value || '',
    }));
  }

  preventSpace(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
  }

  allowDigitsPlusSpace(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    const isAllowedCharacter = /^[0-9+\- ]$/.test(event.key);
    if (!isAllowedCharacter) {
      event.preventDefault();
    }
  }  

}
